import * as globals from '../globals';
import { GeometryList } from './GeometryList';
import { FragmentList } from './FragmentList';
import { MeshCollection3D } from './MeshCollection3D';
import { MeshCollection2D } from './MeshCollection2D';
import { RenderBatch, ConsolidatedRenderBatch } from './RenderBatch';
import { consolidateFragmentList } from './consolidation/FragmentListConsolidation';
import { ProgressState } from '../../application/ProgressState';

import { SceneTraversal } from './SceneTraversal';
import { ModelIteratorBVH } from './ModelIteratorBVH';
import { ModelIteratorLinear } from './ModelIteratorLinear';

import { VBIntersector } from './VBIntersector';
import THREE from "three";
import { RenderFlags } from "./RenderFlags";
import { logger } from "../../logger/Logger";
import { LmvMatrix4 } from "./LmvMatrix4";
import { DynamicGlobalOffset } from "../../application/DynamicGlobalOffset";
import { getByteSize } from './BufferGeometryUtils';
import { MemoryTracker } from './MemoryTracker';
import { createWorker } from '../../file-loaders/main/WorkerCreator';
import { GPU_MEMORY_LIMIT, CONSOLIDATION_MEMORY_LIMIT, GPU_OBJECT_LIMIT, USE_WEBGPU, USE_OUT_OF_CORE_TILE_MANAGER } from "../globals";
import { isEqual } from 'lodash-es';
import { ConsolidationMap } from './consolidation/Consolidation.js';
import { MODEL_CONSOLIDATION_ENABLED, MODEL_CONSOLIDATION_DISABLED } from "../../application/PrivateEventTypes";
import { warnOnce } from '../../application/LoggingUtils';
import { FeatureFlags, InternalFeatureFlags } from "../../application/FeatureFlags";

/** @import { VisibilityManager } from '../../tools/MultiModelVisibilityManager' */
/** @import { MeshCollectionBase } from './MeshCollectionBase' */

export function getUInt8Buffer(typedArray) {
  return new Uint8Array(typedArray.buffer, typedArray.byteOffset, typedArray.byteLength);
}

/** @import { WebGLRenderer } from "../render/WebGLRenderer" */
/** @import { MaterialManager } from "../render/MaterialManager" */
/** @import { Consolidation } from "./consolidation/Consolidation" */
/** @import { OutOfCoreTileManager } from "./out-of-core-tile-manager/OutOfCoreTileManager" */
/** @import { Viewer3DImpl } from "../../application/Viewer3DImpl" */


const av = Autodesk.Viewing;

const CONSOLIDATION_CANCEL_MESSAGE = 'Job canceled. While ';

// For up to 500K fragments, selective bbox invalidation performed clearly faster for large models. (Tested for 10M fragments model)
// If it gets significantly larger, it starts backfiring and we fallback to full bbox recompute.
const MAX_FRAGMENTS_CHANGES_PER_BBOX_UPDATE = 500000;

// TODO: move the logic that decides whether or not to stream somewhere closer to SVF;
// Ideally, RenderModel and GeometryList should be agnostic to the file format.
/*
 * Helper function to determine whether we should enable streamingDraw or upload the whole model to GPU.
 *
 * This function uses values from an SVF package to estimate the expected GPU load. If it is
 * small enough, it returns false. This means that the whole model is uploaded to GPU.
 *
 * If the model size is larger or unknown, we use a heuristic to determine which models are uploaded
 * to GPU and which are rendered from CPU-memory using the (slower) streamingDraw.
 *  @param {number} packFileTotalSize
 *  @param {number} numPrimitives
 *  @param {number} numObjects
 */
function needsStreamingDraw(packFileTotalSize, numPrimitives, numObjects) {
  if (packFileTotalSize) {
    //In pack files, primitive indices use 4 byte integers,
    //while we use 2 byte integers for rendering, so make this
    //correction when estimating GPU usage for geometry
    var estimatedGPUMem = packFileTotalSize - numPrimitives * 3 * 2;

    //If the model is certain to be below a certain size,
    //we will skip the heuristics that upload some meshes to
    //GPU and keep other in system mem, and just push it all to the GPU.
    if (estimatedGPUMem <= GPU_MEMORY_LIMIT && numObjects < GPU_OBJECT_LIMIT) {
      // We don't need streaming draw - model is small enough
      return false;
    }
  }

  return true;
}

var isPointOutsidePlanecuts = (() => {
  const v = new THREE.Vector4();
  return (point, cutplanes) => {
    v.copy(point); // copy already sets w=1 when copying from a Vector3
    for (let i = 0; i < cutplanes.length; i++) {
      if (cutplanes[i].dot(v) > 1e-6) {
        return true;
      }
    }

    return false;
  };
})();

// Counter to assign individual numbers to RenderModel in order of their creation
var nextModelId = 1;

/** @class Represents functionality for WebGL rendering.
 *         Currently produced by loaders (F2DLoader, SvfLoader)
 */
export class RenderModel {

  // NOTE: Don't initialize members here. All construction-related code needs to go into reset.

  // Cached bboxes.
  #visibleBounds; // excluding ghosted once
  #visibleBoundsWithHidden; // full bbox
  #tmpBox; // temp for internal use

  // triggers recomputation of _visibleBounds and _visibleBoundsWithHidden, e.g., if fragment visibility changes.
  #visibleBoundsNeedRecompute;

  // Optimization hint: For smaller per-fragment changes up to MAX_FRAGMENTS_CHANGES_PER_BBOX_UPDATE,
  // we mark affected RenderBatches as dirty and restrict the bbox updates to these batches.
  #fragmentsChangedSinceLastBBoxUpdate;
  #needsFullBoundsRecompute; // Set to true if cannot avoid a full bbox update
  #raycastIteratorNeedsFullBoundsRecompute;

  #iteratorChangedBounds;

  // number of currently highlighted fragments.
  #numHighlighted;

  // use next free Model id
  id = nextModelId++;

  /** @type {GeometryList} */
  #geoms;

  /** @type {FragmentList} */
  #frags;

  /** @type {MeshCollection3D|MeshCollection2D} */
  #meshes;

  // Iterators used for scene traversal.
  /** @type {SceneTraversal} */
  #sceneTraversal;

  // an optional iterator used for ray intersection tests
  // It is not affected by fragment visibility changes to reduce freezes on isolate/show all.
  // This makes raycasts e.g. after isolate slower than they could be, but they are still fast enough.
  // See also #raycastIteratorNeedsFullBoundsRecompute
  /** @type {ModelIteratorBVH} */
  #raycastIterator;

  /** @type {Array<Callback>} */
  #iteratorChangedCallbacks;

  // Only used for consolidated models.
  #consolidationMap; // cached intermediate results of consolidation pre-processing. Enables to quickly rebuild
  // _consolidationIterator when we had to temporarily remove it to free memory.

  // time stamp when consolidation, if any, was started
  #t0_consolidation;

  // Maintained per scene traversal, initialized in ResetIterator()
  #renderCounter; // number of batches rendered since last resetIterator() call. Used to indicate rendering progress for progressive rendering.
  #frustum; // {FrustumIntersector}. Assigned in this.ResetIterator(). Passed to RenderBatches for culling and depth-sorting.
  #drawMode; // drawMode used in this traversal. See Viewer3DImpl.js

  // Cache for transform matrices
  #identityMatrix; // {LmvMatrix4}
  #modelAndPlacementTransform; // {LmvMatrix4}
  #invModelAndPlacementTransform; // {LmvMatrix4}

  // Dynamic Placement: These settings override the corresponding cameras and matrices in this.getData if placement or globalOffset are modified after loading.
  //                    Only used if dynamic placement or offset changes are actually done. Note that placementTransform and globalOffset in this.getData always
  //                    store the values applied during loading and are not affected by setGlobalOffset() or setPlacementTransform() calls.
  #placementTransform; // {LmvMatrix4}
  #globalOffset; // {Vector3}

  // Cached inverse of all loader-baked transforms
  #invPlacementWithOffset;

  // This will be used by the model iterators
  // to create batches. Limited memory will change it.
  RenderBatch = RenderBatch;

  // When enabled, cutplanes should not affect the visibility of the model
  #doNotCut;

  #idRemap;
  #reverseMap;

  // Manages visibility for selected sections/nodes of the model, hiding and showing them on-the-fly.
  // NOTE Viewer3D has its own visibility manager also, but this instance is independent, owned by RenderModel,
  // though initialized by the VisibilityManager from outside.
  /** @type {VisibilityManager|null} */
  visibilityManager;

  #plane;
  #pointOnSheet;

  // The original model's bounding box without placement transform.
  #modelBBoxOriginal;

  // Bounding box baked with placement transform,
  // if for some reason 'modelBoxWithoutPlacementTf' does not exist (missing in loader).
  #modelBBox;

  // Placement transform containing the offset
  #placementTf;

  // TODO storing the whole manifest will become obsolete, once its constant properties are stored here (see config).
  #manifest;

  /** @type {{viewer3DImpl: Viewer3DImpl} */
  loader;

  #currentConsolidationJob;
  #currentConsolidationPromise;
  _currentConsolidationOptions;

  #viewer3DImpl;

  static _consolidationCounter = 0; // used to assign identifier to consolidation jobs
  static _consolidationWorker = null; // worker for consolidation tasks
  static _consolidationJobs = new Map(); // active consolidation jobs
  static _workerRefCount = 0; // number of models potentially using the consolidation worker

  /**
   * Adds a new consolidation job to the list of active jobs.
   * @param {ConsolidationJobContext} job
   * @returns {ConsolidationJobContext}
   */
  static addJob(job) {
    if (!RenderModel._consolidationWorker) {
      RenderModel._consolidationWorker = createWorker('ConsolidationWorker');
    }

    let canceled = false;
    job.stopIfCanceled = (markerName = "") => {
      if (canceled) {
        throw CONSOLIDATION_CANCEL_MESSAGE + markerName;
      }
    };
    job.cancel = () => {canceled = true;};
    job.signalProgress = (progress) => {
      if (job.model.loader.viewer3DImpl) {
        job.model.loader.viewer3DImpl.signalProgress(progress, ProgressState.OPTIMIZING, job.model);
      }
    };
    job.worker = RenderModel._consolidationWorker;
    job.id = RenderModel._consolidationCounter++;
    job.signalProgress(0.1);

    this._consolidationJobs.set(job.id, job);
    return job;
  }

  /**
   * Removes a consolidation job from the list of active jobs.
   * @param {ConsolidationJobContext} job
   */
  static removeJob(job) {
    job.model.loader?.viewer3DImpl.signalProgress(100, ProgressState.OPTIMIZING, this);
    RenderModel._consolidationJobs.delete(job.id);
  }

  /**
   * Counts models using the consolidation worker.
   */
  static _registerConsolidationWorker() {
    RenderModel._workerRefCount++;
  }

  /**
   * Decrements the count of models using the consolidation worker and terminates the worker if no models are using it.
   */
  static async _unregisterConsolidationWorker() {
    this._workerRefCount--;
    if (RenderModel._workerRefCount === 0 && RenderModel._consolidationWorker) {
      // Cancel all pending consolidation jobs and wait for them to finish.
      // If the worker is terminated before the jobs are finished, the jobs promise will never resolve.
      const jobPromises = [];
      this._consolidationJobs.forEach((job) => {
        job.cancel();
        jobPromises.push(job.promise);
      });

      Promise.all(jobPromises).then(() => {
        if (this._consolidationJobs.size === 0) {
          logger.info('Shutting down consolidation worker');
          RenderModel._consolidationWorker.terminate();
          RenderModel._consolidationWorker = null;
        }
      });
    }
  }

  /**
   * Creates a new RenderModel instance.
   *
   * @param {Object} [manifest]
   * @param {Object} [loader]
   */
  constructor(manifest, loader) {
    // Don't put any member initializations in here. All construction code needs to go into reset.
    this.#reset(manifest, loader, true);
  }

  /**
   * Resets a model instance to its post-constructor state.
   *
   * This unloads all model data and allows to reload / refill the model from scratch.
   *
   * @param {Object} [manifest]
   * @param {Object} [loader]
   */
  reset(manifest, loader) {
    this.#reset(manifest, loader, false);
  }

  // This implementation method is private to avoid calling 'reset' twice during construction.
  // If the implementation would be public, it would be invoked by Model and RenderModel in their respective
  // constructors.
  #reset(manifest, loader, skipDispose = false) {
    // Free up resources.
    if (!skipDispose) {
      // Dispose only works on a properly set up model instance, so it can be skipped,
      // for example when calling reset from the constructor.
      this.#dispose();
    }

    RenderModel._registerConsolidationWorker();

    // Initialize model data. All member initialization code goes here.
    this.#visibleBounds = new THREE.Box3();
    this.#visibleBoundsWithHidden = new THREE.Box3();
    this.#tmpBox = new THREE.Box3();

    this.#visibleBoundsNeedRecompute = false;
    this.#fragmentsChangedSinceLastBBoxUpdate = 0;
    this.#needsFullBoundsRecompute = false;

    this.#iteratorChangedBounds = false;

    this.#numHighlighted = 0;

    this.#geoms = null;
    this.#frags = null;
    this.#meshes = null;

    this.#sceneTraversal = new SceneTraversal();

    this.#raycastIterator = null;
    this.#iteratorChangedCallbacks = [];

    this.#consolidationMap = null;
    this.#t0_consolidation = undefined;

    this.#renderCounter = 0;
    this.#frustum = null;
    this.#drawMode = RenderFlags.RENDER_NORMAL; // drawMode used in this traversal. See Viewer3DImpl.js

    this.#identityMatrix = null;
    this.#modelAndPlacementTransform = null;
    this.#invModelAndPlacementTransform = null;

    this.#placementTransform = null;
    this.#globalOffset = null;

    this.#invPlacementWithOffset = null;

    this.#doNotCut = false;

    this.#idRemap = undefined;
    this.#reverseMap = undefined;

    this.visibilityManager = null;

    this.#plane = new THREE.Plane();
    this.#pointOnSheet = new THREE.Vector3();

    this.#modelBBoxOriginal = null;
    this.#modelBBox = null;
    this.#placementTf = null;

    this.#manifest = null;

    this.loader = loader;

    this.#currentConsolidationJob = null;
    this.#currentConsolidationPromise = null;
    this._currentConsolidationOptions = {};

    this.#viewer3DImpl = null;

    this.setManifest(manifest);
  }

  dtor() {
    this.#dispose();
  }

  /**
   * Initializes the render-able data (RenderModel).
   *
   * @param {Viewer3DImpl} viewer3DImpl The viewer3DImpl instance that owns this RenderModel.
   * @returns {void}
   * @internal
   */
  initialize(viewer3DImpl) {
    this.#viewer3DImpl = viewer3DImpl;

    if (this.isLeaflet()) // Leaflets are not initialized
      return;

    // alloc GeometryList. Initially empty, but exposed via GetGeometryList().
    // The loaders use this to add LmvGeometryBuffers directly to the GeometryList later.
    // TODO Make RenderModel agnostic to the SVF file format.
    const numObjects = this.#manifest.numGeoms;
    const disableStreaming = !needsStreamingDraw(
      this.#manifest.packFileTotalSize,
      this.#manifest.primitiveCount,
      numObjects);

    this.#geoms = new GeometryList(numObjects, this.is2d(), disableStreaming, this.isOTG());

    const num_materials = this.#manifest.metadata?.stats?.num_materials ?? 0;
    const num_geoms = this.#manifest.metadata?.stats?.num_geoms ?? 0;
    const fragments = this.#manifest.fragments;

    if (FeatureFlags.isEnabled(InternalFeatureFlags.SceneAPI)) {
      if (this.is2d()) {
        this.#frags = this.#meshes = new MeshCollection2D(fragments, this.id);
      } else {
        this.#frags = this.#meshes = new MeshCollection3D(fragments,
        this.getGeometryList(),
        this.id,
        num_materials,
        this.isOTG(),
        this,
        viewer3DImpl);
      }
    } else {
      this.#frags = new FragmentList(
        fragments,
        this.getGeometryList(),
        this.id,
        num_materials,
        this.is2d(),
        this.isOTG());
    }

    const initialBbox = this.getModelBounds();
    if (initialBbox) {
      this.#visibleBounds.copy(initialBbox);
      this.#visibleBoundsWithHidden.copy(initialBbox);
    }

    const modelIteratorLinear = new ModelIteratorLinear(this);
    this.addTraversalController("Linear", modelIteratorLinear);
  }

  // This exists to allow deferred setting of the manifest, i.e. after creating an initially empty model.
  // Used by Tandem.
  setManifest(manifest) {
    if (!manifest) {
      return;
    }

    this.#manifest = manifest;
    this.#modelBBoxOriginal = manifest.modelSpaceBBox;
    this.#modelBBox = manifest.bbox;
    this.#placementTf = manifest.placementWithOffset;
  }

  /**
   * Adds a new traversal controller, to be used depending on available data and other settings.
   * @param {String} id The unique identifier of this traversal controller.
   * @param {ITraversalController} traversalController The instance of the traversal controller.
   */
  addTraversalController(id, traversalController) {
    this.#sceneTraversal.addTraversalController(id, traversalController);

    // make sure that bbox is obtained from iterator
    this.invalidateBBoxes(true);

    for (const callback of this.#iteratorChangedCallbacks) {
      callback(traversalController);
    }
    this.#dispatchEvent({
      type: Autodesk.Viewing.MODEL_TRAVERSAL_CONTROLLER_ADDED,
      model: this,
      controllerType: id
    });
  }

  // Note: GeometryList or FragmentList are maintained by the RenderModel and should not be modified from outside.
  //       E.g., setting visibility or highlighting flags on FragmentList directly would break some state tracking. (e.g. see this.setVisibility or this.setHighlighted)
  //       The only current exception is done by loaders that add geometry to _geoms directly.
  getGeometryList() {
    return this.#geoms;
  }

  /**
   * Returns the fragment list
   * @returns {FragmentList} The fragment List
   */
  getFragmentList() {
    return this.#frags;
  }

  /**
   * Returns the list of meshes associated with the model
   * @returns {MeshCollectionBase}
   */
  getMeshCollection() {
    return this.#meshes;
  }

  getModelId() {
    return this.id;
  }

  getIterator() {
    return this.#sceneTraversal.getTraversalController();
  }

  isIdRemapValid() {
    return this.#idRemap != null;
  }

  /**
   * Returns the time needed to compute the consolidated model, if consolidation was done.
   * @param {Number} - Time stamp when consolidation was finished
   * @returns {Number} - Time needed or 'undefined'.
   */
  #getTimeConsolidation(t1) {
    if (!this.#t0_consolidation) {
      return undefined;
    }
    const delta = t1 - this.#t0_consolidation;
    return delta;
  }

  #dispose() {
    if (this.isConsolidated()) {
      this.untrackConsolidatedGeometry();
    }

    this._cancelConsolidationJob();
    RenderModel._unregisterConsolidationWorker();

    this.#sceneTraversal.dtor();
    this.#raycastIterator = null;

    this.#geoms = null;

    if (this.#frags) {
      this.#frags.dtor(this.#viewer3DImpl?.geomCache(/* create */false));
      this.#frags = null;
    }
    if (this.#manifest) {
      this.#manifest = null;
    }

    this.#viewer3DImpl = null;
  }

  /*
   * This function is only applicable for 1 iterator type, while other iterators (BVH) ignore it.
  */
  /**
   * Activating a fragment means:
   *  - Store geometry in the FragmentList
   *  - Update summed RenderModel boxes
   *  - Add fragment to iterator, so that it is considered in next traversal
   * See FragmentList.setMesh(..) for param details.
   *
   * Note:
   *  - Can only be used with LinearIterator
   */
  activateFragment(fragId, meshInfo, overrideTransform) {
    if (!this.#frags) {
      return;
    }

    this.#frags.setMesh(fragId, meshInfo, overrideTransform);

    this.#sceneTraversal.addFragment(fragId);

    //update the world bbox
    {
      this.#frags.getWorldBounds(fragId, this.#tmpBox);
      this.#visibleBounds.union(this.#tmpBox);
      this.#visibleBoundsWithHidden.union(this.#tmpBox);
    }
  }

  /*
   * This method covers a product-specific case, used by the Fusion collaboration client.
   * TODO Revise this method for an extension or at least higher-level function, not sitting in the innermost core, such as RenderModel.
   *
   * @deprecated With SCENE_API
   */
  setFragment(fragId, mesh, retainMesh) {
    if (fragId === undefined) {
      fragId = this.getFragmentList().getNextAvailableFragmentId();
    }

    this.#frags.setMesh(fragId, mesh, true, retainMesh);

    this.#sceneTraversal.addFragment(fragId);

    //update the world bbox
    {
      this.#frags.getWorldBounds(fragId, this.#tmpBox);
      this.#visibleBounds.union(this.#tmpBox);
      this.#visibleBoundsWithHidden.union(this.#tmpBox);
    }

    return fragId;
  }

  /**
   * Registers a callback that is invoked when the iterator changes.
   * @param {function} callback The callback to invoke on the event. Called with the new iterator.
   */
  registerIteratorChangedCallback(callback) {
    this.#iteratorChangedCallbacks.push(callback);
  }

  /**
   * Deregisters a callback that has previously been registered via `registerIteratorChangedCallback`.
   * @param {function} callback The callback to deregister.
   */
  removeIteratorChangedCallback = function (callback) {
    const index = this.#iteratorChangedCallbacks.indexOf(callback);

    if (index !== -1) {
      this.#iteratorChangedCallbacks.splice(index, 1);
    }
  };

  setBVH(bvh, initializeOoctm = true) {
    const bvhTraversalController = new ModelIteratorBVH(this, bvh, initializeOoctm);
    this.addTraversalController("BVH", bvhTraversalController);

    if (!USE_WEBGPU && initializeOoctm) {
      // Per Tile consolidation only needs the BVH to get started, but it needs the consolidation iterator to be created.
      // This is done with the following line. This is only temporary till we can remove explicitly consolidating the model.
      this._enableConsolidation(this.#viewer3DImpl?.matman());
    }

    // We need to trigger rerendering because this function might be called during the rendering process
    this.#viewer3DImpl?.invalidate(false, true);
  }

  /**
   *  Starts the scene draw traversal, so that nextBatch() will return the first batch to render.
   *   @param: {UnifiedCamera}      camera       - camera.position was needed for the heuristic to choose between linear iterator and BVH.
   *                                               [HB:] The code is currently commented out, so the param is currently unused.
   *   @param: {FrustumIntersector} frustum      - used by RenderBatches for frustum culling and z-sorting.
   *   @param: {number}             drawMode     - E.g., RENDER_NORMAL. See RenderFlags.js
   */
  resetIterator(camera, frustum, drawMode) {
    this.#renderCounter = 0;
    this.#drawMode = drawMode;
    this.#frustum = frustum;

    this.#sceneTraversal.updateView(camera, frustum, drawMode);

    return this.getIterator();
  }

  resetTransparents() {
    this.#sceneTraversal.resetTransparents();
  }

  /**
   * Sets a dedicated iterator for ray intersections. This can be useful when models need to be intersected
   * frequently. The default iterator is optimized for rasterization, not ray casting.
   * @param {Iterator} iterator The iterator to use for ray intersections.
   */
  setRaycastIterator(iterator) {
    this.#raycastIterator = iterator;
  }

  /**
   * Returns the dedicated iterator for ray intersections.
   * @returns {Iterator} The iterator used for ray intersections.
   */
  getRaycastIterator() {
    return this.#raycastIterator;
  }

  /** Returns the next RenderBatch for scene rendering traversal. Used in RenderScene.renderSome().
   *   Use this.resetIterator() to start traversal first.
   *
   *   @param {Number} frameDeadline - The time at which have completely exhausted the budget for this frame.
   *   @param {Number} consolidationDealine - The time until which we still can perform consolidation without exceeding the frame budget.
   *   @returns {RenderBatch|null|undefined} Next batch to render, undefined if the frame deadline has been exceeded, or null if traversal is finished.
   */
  nextBatch(frameDeadline, consolidationDeadline) {
    while (true) {
      // update render progress counter
      this.#renderCounter++;

      // get next batch from iterator
      const renderBatch = this.#sceneTraversal.nextBatch(consolidationDeadline);

      // stop if there are no further batches
      if (!renderBatch) {
        return null;
      }

      // Tag all produced scenes with modelId. This is used for cross-fading between models by rendering them to separate targets.
      renderBatch.modelId = this.id;

      // Some iterators return a ThreeJS scene instead of a RenderBatch, e.g., when cropping or consolidation is applied.
      // The code for fragment visibility and sorting is only defined if the scene is a RenderBatch.
      // For the case of THREE.Scene, we are done here, because:
      //   - Sorting in THREE.Scene is done by FireFlyRenderer.
      //   - Per-fragment visibility is not supported in this case.
      if (renderBatch instanceof THREE.Scene) {
        return renderBatch;
      }

      const sceneHasVisibleParts = this.applyVisibility(renderBatch, this.#drawMode, this.#frustum);

      // WBOIT clears and re-renders all transparent geometry every frame. If applyVisibility rejects the
      // entire batch (e.g., batch bbox barely outside frustum), those fragments are permanently lost.
      // The BVH node already passed its own frustum test, so always return WBOIT transparent batches.
      if (sceneHasVisibleParts || globals.USE_WBOIT && renderBatch.sortObjects) {
        return renderBatch;
      } else {
        // This code is running in a while loop, so it can happen that there is a large number of
        // invocations to the applyVisibility function which never return a new renderBatch, because
        // in each case all fragments within the batch are invisible. In such a case, this loop
        // might exceed the frameDeadline and we have to break. If a valid renderBatch is returned by
        // applyVisibility, we don't need to check, because the function is being returned anyways.
        if (performance.now() > frameDeadline) {
          return undefined;
        }
      }
    }
  }

  /**
   * Set the MESH_RENDERFLAG based on the current render phase while frustum culling the fragments in the scene.
   * @param {RenderBatch} scene The scene to calculate the visibility for
   * @param {number}             drawMode     - E.g., RENDER_NORMAL. See RenderFlags.js
   * @param {FrustumIntersector} frustum      - used by RenderBatches for frustum culling and z-sorting.
   * @return {boolean} True if any fragments in the scene are visible. False otherwise.
   */
  applyVisibility(scene, drawMode, frustum) {
    // TODO move this into the iterator?
    const isVisible = !scene.applyVisibility(drawMode, frustum);

    // Fragments of F2D scenes must be drawn in original order, so we do not sort it.
    if (this.is2d()) {
      return isVisible;
    }

    // No need to sort on something invisible
    if (!isVisible) {
      return false;
    }

    // TODO Move this to the iterator?
    // For 3D scenes, sort fragments of this batch:
    // Generally opaque batches are sorted once by material,
    // while transparent batches are sorted back to front each frame.
    // Sorting by depth is not necessary when using WBOIT.
    if (!globals.USE_WBOIT && scene.sortObjects && !this.getFragmentList().useThreeMesh) {
      scene.sortByDepth(frustum);
      return true;
    }

    if (!scene.sortDone) {
      scene.sortByMaterial();
    }

    return true;
  }

  /**
   * Remove shadow geometry (if exists) from model bounds.
   *
   * @param {THREE.Box3} bounds
   */
  trimPageShadowGeometry(bounds) {
    if (this.hasPageShadow()) {
      const shadowRatio = Autodesk.Viewing.Private.F2dShadowRatio;
      bounds = bounds.clone();

      // If we have pageShadow, we must have page_dimensions & page_width.
      const pageWidth = this.getMetadata('page_dimensions', 'page_width');
      bounds.max.x -= pageWidth * shadowRatio;
      bounds.min.y += pageWidth * shadowRatio;
    }

    return bounds;
  }

  /**
   * @param:  {bool}        includeGhosted
   * @param:  {bool}        excludeShadow - Remove shadow geometry (if exists) from model bounds.
   * @returns {THREE.Box3}
   *
   * NOTE: The returned box is just a pointer to a member, not a copy!
   */
  getVisibleBounds(includeGhosted, excludeShadow) {
    const sceneIterator = this.getIterator();

    if (this.#visibleBoundsNeedRecompute) {
      // Trigger iterator bbox update.
      // If #needsFullBoundsRecompute is false, it's sufficient to just update the
      // RenderBatches that were marked as dirty in #fragmentBoundsChanged() calls before.
      sceneIterator.updateBounds(undefined, this.#needsFullBoundsRecompute);
    }

    // If anything changed...
    if (this.#visibleBoundsNeedRecompute || this.#iteratorChangedBounds) {

      // clear summed model bboxes
      this.#visibleBounds.makeEmpty();
      this.#visibleBoundsWithHidden.makeEmpty();


      // update summed model bboxes from scene iterator
      sceneIterator.getBounds(this.#visibleBounds, this.#visibleBoundsWithHidden);
      this.#visibleBoundsWithHidden.union(this.#visibleBounds);

      // reset the dirty flags
      this.#visibleBoundsNeedRecompute = false;
      this.#needsFullBoundsRecompute = false;
      this.#fragmentsChangedSinceLastBBoxUpdate = 0;
      this.#iteratorChangedBounds = false;
    }

    let bounds = includeGhosted ? this.#visibleBoundsWithHidden : this.#visibleBounds;

    if (excludeShadow) {
      bounds = this.trimPageShadowGeometry(bounds);
    }

    return bounds;
  }

  /**
   * Performs a 2D ray intersection test for sheet/2D models.
   * @private
   *
   * @param {THREE.Raycaster}  raycaster - The raycaster to use for the intersection test.
   * @param {number[]} [dbIds]  - Optional array of dbIds to filter results.
   * @param {Array} [intersections] - Optional output array to collect all intersections.
   * @param {function} [getDbIdAtPointFor2D] - Optional callback that returns [dbId, modelId] for a given point.
   * @returns {{intersectPoint: THREE.Vector3, point: THREE.Vector3, distance: number, dbId: number, fragId: number, model: RenderModel}|null|undefined}
   */
  rayIntersect2D(raycaster, dbIds, intersections, getDbIdAtPointFor2D) {
    // A sheet is assumed to be, when loaded, pointing z-up on 0,0;
    // Get original bounding box
    const bbox = this.getModelBounds(true);

    const center = bbox.getCenter(new THREE.Vector3());
    let point = new THREE.Vector3();
    this.#plane.normal.set(0, 0, 1);
    this.#plane.constant = -center.z;

    const tr = this.getModelToViewerTransform();
    if (tr) {
      this.#plane.applyMatrix4(tr);
    }

    point = raycaster.ray.intersectPlane(this.#plane, point);
    if (point) {
      this.#pointOnSheet.copy(point);
      const invTr = this.getInverseModelToViewerTransform();
      if (invTr) {
        this.#pointOnSheet.applyMatrix4(invTr);
        this.#pointOnSheet.z = center.z; // Avoid numerical problems
      }
      if (bbox.containsPoint(this.#pointOnSheet)) {
        const cutplanes = this.#frags?.getMaterial(0)?.cutplanes; // Get cutplanes from first material
        if (cutplanes && isPointOutsidePlanecuts(point, cutplanes)) {
          return;
        }
        const distance = raycaster.ray.origin.distanceTo(point);
        if (distance < raycaster.near || distance > raycaster.far) {
          return;
        }

        let dbId, fragId;
        if (getDbIdAtPointFor2D) {// This is an optional callback
          const res = getDbIdAtPointFor2D(point);
          dbId = res[0];
          if (dbIds && dbIds.length > 0 && !dbIds.includes(dbId)) {// Filter according to passed array
            return;
          }

          const modelId = res[1]; // modelId is 0 if the idtarget[1] is not used
          if (modelId !== 0 && modelId !== this.id) {
            // In the case where another model is in front of this one, the dbId we get here
            // will be for that model instead, so just ignore this result
            return;
          } else {
            fragId = this.#frags?.fragments.dbId2fragId[dbId];
          }
        }

        const intersection = {
          intersectPoint: point, // Backwards compatibility
          point,
          distance,
          dbId: dbId && this.remapDbIdFor2D(dbId),
          fragId,
          model: this
        };

        if (intersections) {
          intersections.push(intersection);
        }

        return intersection;
      }
    }

    return null;
  }

  /**
   * Performs a raytest and returns an object providing information about the closest hit.
   *
   * NOTE: We currently ignore hitpoints of fragments that are visible (MESH_VISIBLE==true) and not highlighted (MESH_HIGHLIGHTED==false).
   *
   * @param {THREE.RayCaster} raycaster
   * @param {bool}            ignoreTransparent
   * @param {number[]}        [dbIds]             - Array of dbIds. If specified, only fragments with dbIds inside the filter are checked.
   *                                                If the model data has no instanceTree, this is just a whitelist of explicit fragment ids.
   *                                                Note that a hitpoint will also be returned if it's actually occluded by a fragment outside the filter.
   * @param {Array}           [intersections]     - Optional return array with all found intersections.
   * @param {function}        [getDbIdAtPointFor2D] - Optional callback. For 2D models, to return the dbId and modelId in an array.
   * @param {Object}          [options]                       - Additional ray intersection options
   * @param {function}        [options.filter]                - Optional filter function (hitResult) => bool. (see VBIntersector for hitresult content)
   * @param {boolean}         [options.skipWithoutRaycastBVH] - Optional flag indicating whether raycasting should be skipped if we only have a rendering BVH.
   *
   * @returns {Object|null}   Intersection result object providing information about closest hit point. Properties:
   *                           - {number}   fragId
   *                           - {Vector3}  point
   *                           - {number}   dbId
   *                           - {model}    model - pointer to this RenderModel
   *                          (created/filled in VBIntersector.js, see for details)
   */
  // Add "meshes" parameter, after we get meshes of the object using id buffer,
  // then we just need to ray intersect this object instead of all objects of the model.
  rayIntersect(raycaster, ignoreTransparent, dbIds, intersections, getDbIdAtPointFor2D, options) {
    if (this.ignoreRayIntersect || options?.skipWithoutRaycastBVH && !this.#raycastIterator) {
      return null;
    }

    if (this.is2d()) {
      return this.rayIntersect2D(raycaster, dbIds, intersections, getDbIdAtPointFor2D);
    }
    // make sure that the cached overall bboxes are up-to-date.
    if (this.#raycastIterator) {
      if (this.#raycastIteratorNeedsFullBoundsRecompute) {
        this.#raycastIterator.updateBoundsRaycastOnly();
        this.#raycastIteratorNeedsFullBoundsRecompute = false;
      }
    } else {
      if (this.#visibleBoundsNeedRecompute) {
        this.getVisibleBounds();
      }
    }

    // alloc array to collect intersection results
    var intersects = [];
    var i;

    // Restrict search to certain dbIds if specified...
    if (dbIds && dbIds.length > 0) {

      // Collect the mesh fragments for the given database ID node filter.
      var instanceTree = this.getInstanceTree();
      var fragIds = [];
      if (instanceTree) {
        for (i = 0; i < dbIds.length; i++) {
          instanceTree.enumNodeFragments(dbIds[i], function (fragId) {
            fragIds.push(fragId);
          }, true);
        }
      } else {
        //No instance tree -- treat dbIds as fragIds
        fragIds = dbIds;
      }

      //If there are multiple fragments it pays to still use
      //the bounding volume hierarchy to do the intersection,
      //because it can cull away entire fragments by bounding box,
      //instead of checking every single fragment triangle by triangle
      if (fragIds.length > 2) {//2 is just an arbitrary value, assuming checking 2 fragments is still cheap than full tree traversal
        let iterator = this.#raycastIterator || this.getIterator();
        iterator.rayCast(raycaster, intersects, dbIds);
      } else {
        // The filter restricted the search to a very small number of fragments.
        // => Perform raytest on these fragments directly instead.
        for (i = 0; i < fragIds.length; i++) {
          var mesh = this.#frags.getVizmesh(fragIds[i]);
          if (!mesh)
          continue;
          var res = VBIntersector.rayCast(mesh, raycaster, intersects, options);
          if (res) {
            intersects.push(res);
          }
        }
      }

    } else {
      // no filter => perform raytest on all fragments
      let iterator = this.#raycastIterator || this.getIterator();
      iterator.rayCast(raycaster, intersects, undefined, options);
    }

    // stop here if no hit was found
    if (!intersects.length)
    return null;

    // sort results by distance.
    intersects.sort(function (a, b) {return a.distance - b.distance;});

    //pick the nearest object that is visible as the selected.
    var allIntersections = !!intersections;
    intersections = intersections || [];

    let result = null;
    for (i = 0; i < intersects.length; i++) {

      var fragId = intersects[i].fragId;

      // [HB:] Since we skip all meshes that are not flagged as visible, shouldn't we
      //       better exclude them from the raycast in the first place?
      if (this.#frags.isFragVisible(fragId)) {

        // skip transparent hits if specified
        var material = this.#frags.getMaterial(fragId);
        if (ignoreTransparent && material.transparent)
        continue;

        var intersect = intersects[i];

        // check against cutplanes
        var isCut = false;
        if (material && material.cutplanes) {
          isCut = isPointOutsidePlanecuts(intersect.point, material.cutplanes);
        }

        if (!isCut) {
          intersections.push(intersect);
          if (!result) {
            result = intersect;
          }
        }

        intersect.model = this;

        if (!allIntersections && intersections.length > 0) {
          // result is the closest hit that passed all tests => done.
          break;
        }
      }
    }

    return result;
  }


  /** Set highlighting flag for a fragment.
   *   @param   {number} fragId
   *   @param   {bool}   value
   *   @returns {bool}   indicates if flag state changed
   */
  setHighlighted(fragId, value) {
    if (!this.#frags) {
      return false;
    }

    var changed = this.#frags.setHighlighted(fragId, value);

    if (changed) {
      if (value)
      this.#numHighlighted++;else

      this.#numHighlighted--;
    }

    return changed;
  }

  /** Sets MESH_VISIBLE flag for a fragment (true=visible, false=ghosted) */
  // This function should probably not be called outside VisibilityManager
  // in order to maintain node visibility state.
  setVisibility(fragId, value) {
    if (this.#frags) {
      const changed = this.#frags.setVisibility(fragId, value);
      if (changed) {
        this.#fragmentVisibleBoundsChanged(fragId);
      }
    } else if (this.isLeaflet()) {
      this.#sceneTraversal.setVisibilityLeaflet(value);
      this.invalidateBBoxes();
    }
  }

  /** Sets MESH_VISIBLE flag for all fragments (true=visible, false=ghosted) */
  setAllVisibility(value) {
    if (this.#frags) {
      this.#frags.setAllVisibility(value);
    } else if (this.isLeaflet()) {
      this.#sceneTraversal.setVisibilityLeaflet(value);
    }

    this.invalidateBBoxes();
  }

  /** Sets the MESH_HIDE flag for all fragments that a flagged as line geometry.
   *  Note that the MESH_HIDE flag is independent of the MESH_VISIBLE flag (which switches between ghosted and fully visible)
   *
   *  @param {bool} hide - value to which the MESH_HIDE flag will be set. Note that omitting this param would SHOW the lines, so
   *                       that you should always provide it to avoid confusion.
   */
  hideLines(hide) {
    if (this.#frags) {
      this.#frags.hideLines(hide);
    }
  }

  /** Sets the MESH_HIDE flag for all fragments that a flagged as point geometry.
   *  Note that the MESH_HIDE flag is independent of the MESH_VISIBLE flag (which switches between ghosted and fully visible)
   *
   *  @param {bool} hide - value to which the MESH_HIDE flag will be set. Note that omitting this param would SHOW the points, so
   *                       that you should always provide it to avoid confusion.
   */
  hidePoints(hide) {
    if (this.#frags) {
      this.#frags.hidePoints(hide);
    }
  }

  /** Returns if one or more fragments are highlighted.
   *   returns {bool}
   *
   * Note: This method will only work correctly as long as all highlighting changes are done via this.setHighlighted, not on FragmentList directly.
   */
  hasHighlighted() {
    return !!this.#numHighlighted;
  }

  /** Returns true if MESH_VISIBLE flag is set for all fragments. */
  areAllVisible() {

    // When using a custom iterator, we don't have per-fragment visibility control.
    // We assume constantly true in this case.
    if (!this.#frags) {
      return true;
    }

    return this.#frags.areAllVisible();
  }

  /** Direct access to all RenderBatches. Used by ground shadows and ground reflection.
    * @returns {RenderBatch[]}
    */
  getGeomScenes() {
    return this.#sceneTraversal.getScenes();
  }

  /** Get progress of current rendering traversal.
    *  @returns {number} in [0,1]
    */
  getRenderProgress() {
    const progress = this.#renderCounter / this.#sceneTraversal.getSceneCount();

    // the renderCounter can become > scene count.
    return progress > 1.0 ? 1.0 : progress;
  }

  /**
   *  @params  {number} timeStamp
   *  @returns {bool}   true if the model is a leaflet scene and it needs a redraw, false otherwise.
   */
  update(timeStamp) {
    const needsRedraw = this.#sceneTraversal.needsRedrawLeaflet(timeStamp);
    return needsRedraw;
  }


  /** Highlight an object with a theming color that is blended with the original object's material.
   *   @param {number}        dbId
   *   @param {THREE.Vector4} themingColor (r, g, b, intensity), all in [0,1]
   *   @param {boolean} [recursive] - Should apply theming color recursively to all child nodes.
   */
  setThemingColor(dbId, color, recursive) {
    if (this.#frags) {
      // When using 2d with Otg db, we need to remap, because the vertex-buffers still contain otg.
      dbId = this.reverseMapDbIdFor2D(dbId);

      const it = this.getInstanceTree();
      if (recursive && it) {
        it.enumNodeChildren(dbId, (childDbId) => {
          this.#frags.setThemingColor(childDbId, color);
        }, true);
        return;
      }

      this.#frags.setThemingColor(dbId, color);
      return;
    }

    if (this.#sceneTraversal.setThemingColorLeaflet(color)) {
      return;
    }

    logger.warn("Theming colors are not supported by this model type.");
  }

  /** Revert all theming colors.
   */
  clearThemingColors() {
    if (this.#frags) {
      this.#frags.clearThemingColors();
    } else {
      this.#sceneTraversal.clearThemingColorsLeaflet();
    }
  }

  /** Access to leaflet-specific functionality. Returns null if RenderModel is no leaflet. */
  getLeaflet() {
    return this.#sceneTraversal.getLeafletIterator();
  }

  /**
   * For internal use only.
   *
   * Determines for each geometry whether to store it on GPU or only CPU-side. The heuristic is the same that is
   * always used by GeometryList. However, when using consolidation, we first spend GPU Ram for the consolidated
   * meshes (with are used more for rendering). The original fragment geometry is only stored on GPU
   * if enough budget is left.
   * @private
   */
  chooseMemoryTypes() {
    // If the out of core tile manager is used, memory management is
    // exclusively done by the tile manager.
    if (globals.USE_OUT_OF_CORE_TILE_MANAGER && !this.is2d()) {
      return;
    }

    const consolidation = this.#sceneTraversal.getConsolidation();
    var geomList = this.getGeometryList();

    // some geometries are shared by consolidation and original fragments. We track their ids to
    // make sure that we don't process them twice.
    var geomShared = [];

    let geom;

    // Untrack geometries first. GPU usage is reevaluated below.
    // In case of consolidation, this frees up GPU budget for the consolidated geometry, which has higher priority.
    for (i = 1; i < geomList.getCount(); i++) {// skip index 0, because it is reserved for INVALID_GEOM_ID
      geom = geomList.getGeometry(i);
      if (geom) {
        // Force-untrack geometry on the GPU, even if it is shared with other models
        MemoryTracker.untrackGeometry(geomList, geom, false, !geom.streamingDraw, true);
        geom.streamingDraw = geom.streamingIndex = undefined; // flag geometry as untracked
      }
    }

    const fl = this.getFragmentList();
    if (consolidation) {
      for (var i = 0; i < consolidation.meshes.length; i++) {

        var mesh = consolidation.meshes[i];
        geom = mesh?.geometry || fl.getGeometry(mesh.fragId);

        // compute byteSize if not available.
        if (!geom.byteSize) {
          geom.byteSize = getByteSize(geom);
        }
        // If the mesh has a well-defined fragId, this geometry is shared with a fragment that could
        // not be consolidated with others.
        var isSharedFragmentGeometry = Number.isInteger(mesh.fragId);

        // Look at shared geometry only once
        if (isSharedFragmentGeometry) {
          // We cannot use the svfid for SVF2 geometries, because it's not necessarily unique within a model
          const key = geom.id !== undefined ? geom.id : geom.svfid;
          if (geomShared[key]) {
            continue;
          } else {
            geomShared[key] = true;
          }
        }

        MemoryTracker.setMemoryType(geomList, geom, geom.numInstances || 1, this.is2d());

        if (isSharedFragmentGeometry) {
          continue;
        }

        if (geom.streamingDraw) {
          // CPU data won't be discarded, so we have to track it.
          MemoryTracker.trackGeometry(geomList, geom);
        } else {
          // consolidated meshes are purely used for rendering. So, we can discard
          // the CPU-side copy as soon as the data are on GPU. Note that we must not
          // do this for shared original fragment geometry - which is exposed to the client.
          geom.discardAfterUpload = true;
        }
      }
    }

    // Finally, revise the memory type for the original GeometryList again. This time, we consider
    // the workload that we already spent on for the consolidation and only allow geometry to be stored on GPU if
    // our budget is not consumed yet.
    for (i = 1; i < geomList.getCount(); i++) {// skip index 0, because it is reserved for INVALID_GEOM_ID

      // get next geom
      geom = geomList.getGeometry(i);
      if (!geom) {
        continue;
      }

      // if this geometry is shared by the consolidation, the memory type has already been set in the loop above.
      const key = geom.id !== undefined ? geom.id : i;
      if (!geomShared[key]) {
        MemoryTracker.setMemoryType(geomList, geom, geomList.getInstanceCount(i), this.is2d());
      }

      if (geom.streamingDraw) {
        // A geometry might already have been GPU-uploaded and displayed during progressive loading.
        // If we now decided to keep this geometry CPU side, make sure that we don't keep any of these on GPU anymore.
        geom.dispose();
      }
    }
  }

  /**
   * We only want to reconsolidate a model if the parameters have changed
   * @param {Number} byteLimit - The memory limit for consolidation
   * @returns {Boolean} - True if the consolidation options have changed
   */
  #consolidationOptionsChanged(byteLimit) {
    return this._currentConsolidationOptions.byteLimit !== byteLimit;
  }

  /**
   * Cancels the ongoing consolidation job, if available.
   */
  _cancelConsolidationJob() {
    if (this.#currentConsolidationJob && !this.#currentConsolidationJob.canceled) {
      logger.info("Canceling consolidation job.", this.#currentConsolidationJob.id);
      this.#currentConsolidationJob.cancel();
    }
  }

  _enableConsolidation(materials, consolidation) {
    this.#sceneTraversal.enableConsolidation(this.#frags, consolidation, this.#sceneTraversal.getTraversalController('BVH'), materials);
    this.#dispatchEvent({ type: MODEL_CONSOLIDATION_ENABLED, model: this });
  }

  /**
   * This function creates an internal copy of the FragmentList that is consolidated to reduce the
   * shape count as far as possible. This takes more memory, but may strongly accelerate rendering
   * for models with many small shapes.
   *
   * NOTE: For making consolidation effective, it should ideally be activated via the load options already.
   *       This will automatically adjust the depth of the spatial hierarchy. Without that, the scene traversal
   *       may still be slow and the performance gain much smaller.
   *
   * @param {MaterialManager} materials
   * @param {number}          [byteLimit]                   - Merging geometries is the most efficient technique in terms
   *                                                          of rendering performance. But, it can strongly increase
   *                                                          the memory consumption, particularly because merged
   *                                                          geometry cannot be shared, i.e. multiple instances of
   *                                                          a single geometry must be replicated per instance for merging.
   *                                                          Therefore, the memory spent for merging is restricted.
   *                                                          A higher value may make rendering faster, but increases (also GPU) memory
   *                                                          workload.
   * @param {WebGLRenderer}   glRenderer
   * @param {boolean}         [useDeferredConsolidation]    - Optional: If true, consolidation will only compute some initial data
   *                                                          and leave the actual data crunching to the first time a consolidated
   *                                                          mesh needs to get rendered. Note: the intent is to verify the proper
   *                                                          function of this option and make it the only implementation then.
   * @param {boolean} [forceConsolidationMapRecomputation]  - do we need to recompute the consolidation map?
   * @returns {Promise} - Resolves when consolidation is done.
   */
  async consolidateAsync(materials, byteLimit, glRenderer, useDeferredConsolidation = false, forceConsolidationMapRecomputation = false) {
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      warnOnce("Full model consolidation is only used for legacy behavior.");
      return;
    }

    byteLimit = byteLimit || CONSOLIDATION_MEMORY_LIMIT;

    if (this.isConsolidating()) {
      this._cancelConsolidationJob();
    }
    forceConsolidationMapRecomputation ||= this.#consolidationOptionsChanged(byteLimit);

    const newConsolidationJob = {
      byteLimit,
      forceConsolidationMapRecomputation,
      syncTimeSlice: Infinity,
      consolidationMap: this.#consolidationMap,
      useDeferredConsolidation,
      model: this
    };

    logger.info('Starting consolidation', newConsolidationJob.id);
    this.#t0_consolidation = performance.now();

    this.#currentConsolidationJob = RenderModel.addJob(newConsolidationJob);

    // consolidate fragment list
    let consolidationPromise;
    this.#currentConsolidationPromise = this.#currentConsolidationJob.promise = consolidationPromise = consolidateFragmentList(
      this,
      materials,
      newConsolidationJob
    ).then((consolidation) => {
      if (newConsolidationJob.canceled) {
        logger.debug('Skipping results of canceled consolidation', newConsolidationJob.id);
        return;
      }

      // If the model has already been consolidated, we first
      // unconsolidate the existing model to free the resources allocated
      // for the existing consolidation
      if (this.isConsolidated()) {
        this.unconsolidate(false, false, false);
      }

      // cache some intermediate results. Consolidations are memory-intensive, so it can be necessary to temporarily
      // remove them to free memory. By caching intermediate results, we can rebuild them faster.
      this.#consolidationMap = consolidation.consolidationMap;

      // consolidationBVH is only set if consolidationMap was recomputed and not created from cache
      const bvh = newConsolidationJob.consolidationBVH;
      if (bvh) {
        this.setBVH(bvh);
      }

      this._enableConsolidation(materials, consolidation);

      // determine which geometries we upload to GPU. All remaining ones are stored CPU-side
      // and rendered using streaming-draw (slower, but better than GPU memory overload)
      this.chooseMemoryTypes();

      this._currentConsolidationOptions.byteLimit = byteLimit;

      logger.info('Consolidation finished', newConsolidationJob.id);

      // analytics
      const properties = {
        urn: this.getData()?.urn,
        is_enabled_HLOD: false,
        consolidation_time: this.#getTimeConsolidation(performance.now())
      };
      Autodesk.Viewing.Private.analytics.track("viewer.model.consolidation", properties);

    }).finally(() => {
      // If a reconsolidation is triggered, it can happen that we already assigned
      // new currentConsolidationPromise and currentConsolidationJob entries and thus
      // do not want to reset them here.
      if (this.#currentConsolidationPromise === consolidationPromise) {
        this.#currentConsolidationPromise = null;
      }
      if (this.#currentConsolidationJob === newConsolidationJob) {
        this.#currentConsolidationJob = null;
      }
      RenderModel.removeJob(newConsolidationJob);
    }).catch((err) => {
      if (err?.startsWith?.(CONSOLIDATION_CANCEL_MESSAGE))
      logger.info('Consolidation canceled.');else

      logger.error("Consolidation failed.", err);
    });

    return this.#currentConsolidationPromise;
  }

  /**
   * For internal use only.
   * @private
   */
  untrackConsolidatedGeometry() {
    if (USE_OUT_OF_CORE_TILE_MANAGER)
    return;

    const consolidation = this.#sceneTraversal.getConsolidation();
    for (let i = 0; i < consolidation.meshes.length; i++) {
      const mesh = consolidation.meshes[i];
      // If the mesh has a fragId, it is also used in the original fragment list, so don't untrack it.
      const isSharedFragmentGeometry = Number.isInteger(mesh.fragId);
      if (!isSharedFragmentGeometry) {
        const geom = mesh.geometry;
        MemoryTracker.untrackGeometry(this.getGeometryList(), geom, geom.streamingDraw);
      }
    }
  }

  /**
   * See consolidateAsync
   * Will forward to consolidateAsync for some degree of backwards compatibility, but is not synchronous anymore.
   *
   * @deprecated Use consolidateAsync instead
   */
  consolidate(materials, byteLimit, glRenderer, useDeferredConsolidation = false) {
    console.warn('RenderModel.consolidate is deprecated. Use consolidateAsync instead.');
    return this.consolidateAsync(materials, byteLimit, glRenderer, useDeferredConsolidation);
  }

  /**
   * Removes consolidation to free memory. Depending on the use case we may retain the consolidation map.
   *
   * @param {Boolean} [updateGPUMemoryAssignment=false] - If true, GPU resource usage is re-evaluated and geometries
   *  might be moved out of or into the GPU.
   * @param {Boolean} [deleteMap=false] - If true, the consolidation map is removed.
   * @param {Boolean} [cancelRunningJob=true] - If true, a currently running consolidation job is canceled
   */
  unconsolidate(updateGPUMemoryAssignment = false, deleteMap = false, cancelRunningJob = true) {
    if (USE_OUT_OF_CORE_TILE_MANAGER) {
      warnOnce("Unconsolidate is not supported when incremental consolidation or WebGPU is enabled.");
      return;
    }

    // Cancel ongoing consolidation job
    if (cancelRunningJob && this.isConsolidating()) {
      this._cancelConsolidationJob();
    }

    // If we are not consolidated, we don't need to do anything
    if (!this.isConsolidated()) {
      return;
    }

    this.untrackConsolidatedGeometry();

    this.#sceneTraversal.disableConsolidation();
    this.#dispatchEvent({ type: MODEL_CONSOLIDATION_DISABLED, model: this });

    // if we remove the consolidation map, consolidation will start from scratch next time
    if (deleteMap) {
      this._currentConsolidationOptions = {};
      this.#consolidationMap = null;
    }

    // unconsolidating can free up gpu memory, so we should reevaluate memory usage
    if (updateGPUMemoryAssignment)
    this.chooseMemoryTypes();
  }

  isConsolidated() {
    return this.#sceneTraversal.isEnabledConsolidation();
  }

  /**
   * Checks whether a consolidation is currently in progress.
   * @returns {Boolean} True if the model is currently consolidating, false otherwise.
   */
  isConsolidating() {
    return this.#currentConsolidationPromise != null;
  }

  /**
   * Returns a promise resolving when the current consolidation is finished or immediately if none is in progress
   * A new consolidation might be triggered in the meantime, so after the promise is resolved `isConsolidating`
   * should be checked again
   *
   * @returns {Promise} Resolves when the current consolidation is finished or not in progress
   */
  getConsolidationPromise() {
    return this.#currentConsolidationPromise || Promise.resolve();
  }

  /**
   * Wait until the consolidation is available. This function will also handle the case
   * where a consolidation is restarted while we are waiting for the current one. It will
   * only return when the consolidation is actually available and not interrupt the waiting
   * when the consolidation gets restarted.
   */
  async waitUntilConsolidationAvailable() {
    if (USE_WEBGPU) {
      // With WebGPU, the model will never be consolidated, but we still need to wait for the BVH to be available.
      // No loop required since consolidation is never restarted.
      if (this.isConsolidating()) {
        await this.#currentConsolidationPromise;
      }
      return;
    }
    while (!this.isConsolidated()) {
      if (this.isConsolidating()) {
        await this.#currentConsolidationPromise;
      } else {
        console.warn("Waiting for consolidation, but no consolidation triggered.");
        return;
      }
    }
  }

  /**
   * Returns the consolidation for this model
   * @param {number} [bvhNodeId] - The id of the bvh node to get the consolidation for. If not specified, the consolidation for the entire model is returned.
   *                               (if incremental consolidation is disabled) or an array with consolidations for each tile is returned (if incremental consolidation is enabled).
   * @returns {Consolidation|Consolidation[]|undefined} The consolidation for this model/node, an array with per tile consolidations or undefined if there is no consolidation
   */
  getConsolidation(bvhNodeId) {
    return bvhNodeId === undefined ? this.#sceneTraversal.getConsolidation() : this.#sceneTraversal.getConsolidation(bvhNodeId);
  }

  /**
   * Sets the consolidation for a specific bvh node
   * @param {number} bvhNodeId
   * @param {Consolidation|undefined} consolidation
   */
  _setConsolidation(bvhNodeId, consolidation) {
    this.#sceneTraversal.setConsolidation(bvhNodeId, consolidation);
  }

  // Store mapping of F2D/PDF/SVF dbids to OTG property database v2 dbids
  setDbIdRemap(dbidOldToNew) {
    this.#idRemap = dbidOldToNew;
  }

  // Map old SVF dbId to actual dbId as used
  //by v2/OTG property databases.
  remapDbId(dbId) {
    if (this.#idRemap && dbId > 0 && dbId < this.#idRemap.length)
    return this.#idRemap[dbId];

    return dbId;
  }

  //F2D only -- maps ID stored in F2D vertex buffers to actual dbId as used
  //by v2/OTG property databases.
  remapDbIdFor2D(dbId) {
    if (this.is2d()) return this.remapDbId(dbId);

    return dbId;
  }

  reverseMapDbId(dbId) {
    if (!this.#idRemap || dbId <= 0)
    return dbId;

    if (!this.#reverseMap) {
      this.#reverseMap = {};
      for (var i = 0; i < this.#idRemap.length; i++)
      this.#reverseMap[this.#idRemap[i]] = i;
    }

    return this.#reverseMap[dbId];
  }

  reverseMapDbIdFor2D(dbId) {
    if (this.is2d()) return this.reverseMapDbId(dbId);

    return dbId;
  }

  updateRenderProxy(proxy, fragId) {
    this.#sceneTraversal.updateRenderProxy(proxy, fragId);
  }

  // fragment visibility changed and visible bounds need update.
  // bbox changes caused by a frag transform change need to go through invalidateBBoxes.
  #fragmentVisibleBoundsChanged(fragId) {

    // If a full bbox recompute is pending anyway, there's nothing to gain here.
    if (this.#needsFullBoundsRecompute) {
      return;
    }

    // Fine-grained bbox updates are only supported for BVH iterators
    // For ModelIteratorLinear, fallback to full recompute
    const sceneIterator = this.getIterator();
    const supportsGranularUpdates = sceneIterator instanceof ModelIteratorBVH;
    if (!supportsGranularUpdates) {
      this.invalidateBBoxes();
      return;
    }

    // For both iterators, mark bbox dirty for the RenderBatch containing this fragment
    sceneIterator.fragmentBoundsChanged(fragId);

    this.#visibleBoundsNeedRecompute = true;

    // Track number of small changes
    // Fall back to full update if fine-grained changes get too many
    this.#fragmentsChangedSinceLastBBoxUpdate++;
    if (this.#fragmentsChangedSinceLastBBoxUpdate > MAX_FRAGMENTS_CHANGES_PER_BBOX_UPDATE) {
      this.#needsFullBoundsRecompute = true;
    }
  }

  // Call this whenever you modified shapes, e.g., by setting/changing an animation transform.
  // This makes sure that all hierarchical bboxes are updated.
  // Without this, shapes may incorrectly classified as invisble, so that they may disappear or are missed by selection.
  invalidateBBoxes(skipRaycastIterator = false) {
    this.#visibleBoundsNeedRecompute = true;

    // We don't have any hint to restrict bbox updates - so we must update all
    this.#needsFullBoundsRecompute = true;
    if (!skipRaycastIterator) {
      this.#raycastIteratorNeedsFullBoundsRecompute = true;
    }
  }

  // Make RenderModel re-fetch the bounds from the iterator. Called by the iterator if it changed the bounds.
  iteratorChangedBounds() {
    this.#iteratorChangedBounds = true;
  }

  /**
   * Change the paper visibility for a 2D sheet
   */
  changePaperVisibility(show) {
    if (this.is2d()) {
      this.#frags?.setObject2DVisible(-1, show);
    }
  }

  hasPaperTransparency() {
    if (!this.is2d()) {
      return false;
    }

    const paperOpacity = this.#frags?.dbIdOpacity[-1] ?? 1;

    return paperOpacity > 0 && paperOpacity < 1;
  }

  /** Set a new model transform.
   *
   * @param {THREE.Matrix4} [matrix] - If null, model matrix is cleared.
   */
  setModelTransform(matrix) {
    this.#sceneTraversal.setModelTransformLeaflet(matrix);

    if (!this.isLeaflet()) {
      this.#frags.setModelMatrix(matrix);
      this.#sceneTraversal.updateModelTransformConsolidation();
    }

    // Recompute all bboxes
    this.invalidateBBoxes();
    this.getVisibleBounds(true);
    this.#modelAndPlacementTransform = null;
    this.#invModelAndPlacementTransform = null;
  }

  getModelTransform() {
    const matrix = this.#sceneTraversal.getModelTransformLeaflet();
    if (matrix) {
      return matrix;
    }

    return this.#frags?.matrix;
  }

  /**
   * Returns the model boundary, customized by the given options.
   *
   * @param {boolean?}[ignoreTransform] - Set to true to return the original bounding box in model space coordinates.
   * @param {boolean?}[excludeShadow] - Remove shadow geometry (if exists) from model bounds.
   * @param {THREE.Box3}[modelBoxOriginal] - The original model's bounding box without placement transform.
   *
   * @returns {THREE.Box3} - Bounding box of the model if available, otherwise null.
   */
  getModelBounds(ignoreTransform, excludeShadow) {
    if (!this.#modelBBoxOriginal && !this.#modelBBox) {
      return null;
    }

    const modelBBoxOut = new THREE.Box3();

    modelBBoxOut.copy(this._getModelBBoxOriginal() || this.#modelBBox);

    // Remove shadow geometry if needed.
    if (excludeShadow) {
      modelBBoxOut.copy(this.trimPageShadowGeometry(modelBBoxOut));
    }

    // If ignore transform is set, we are done.
    if (ignoreTransform) {
      return modelBBoxOut;
    }

    // Apply placement transform only if the modelSpace bounding box was used.
    if (this.#placementTf && this.#modelBBoxOriginal) {
      modelBBoxOut.applyMatrix4(this.#placementTf);
    }

    // Apply dynamic model transform.
    const modelMatrix = this.getModelTransform();
    if (modelMatrix) {
      modelBBoxOut.applyMatrix4(modelMatrix);
    }

    return modelBBoxOut;
  }

  /**
   * Passes the latest model boundary, excluding the offset transform.
   * Note this value may be changing, as loading progresses, so we want to get the latest one always if available.
   *
   * @param {THREE.Box3}[modelBoxOriginal] - The original model's bounding box without placement transform.
   */
  updateModelBBoxOriginal(modelBoxOriginal) {
    this.#modelBBoxOriginal = modelBoxOriginal;
  }

  /**
   * Returns the original model's bounding box without placement transforms. May trigger a bounding box update if transforms changed.
   * @returns {THREE.Box3} The original model's bounding box without placement transform.
   * @internal
   */
  _getModelBBoxOriginal() {
    // Ensure mesh collection has recomputed bbox if transforms changed
    this.getMeshCollection()?.ensureModelBBoxUpToDate?.();

    return this.#modelBBoxOriginal;
  }

  getInverseModelTransform() {
    const matrix = this.#sceneTraversal.getInverseModelTransformLeaflet();
    if (matrix) {
      return matrix;
    }

    return this.#frags?.getInverseModelMatrix();
  }

  /*
   * Returns current placementTransform. By default, this is the placementMatrix applied at load time,
   * but may be overwritten if resetPlacement was called after loading.
   * Returned value must not be modified from outside.
   */
  getPlacementTransform() {
    this.#identityMatrix = this.#identityMatrix || new LmvMatrix4(true);
    return this.#placementTransform || this.getData().placementTransform || this.#identityMatrix;
  }

  /*
   * Returns the globalOffset applied to the model. This may be the one applied at load time or
   * a dynamic globalOffset applied afterwards.
   *  @returns {Vector3}
   */
  getGlobalOffset() {
    return this.#globalOffset || this.getData().globalOffset;
  }

  /**
   * Change the placement matrix of the model. This overrides the placement transform applied at loadTime.
   *  @param {LmvMatrix4} matrix         - Note that you need 64-Bit precision for large values.
   *  @param {Vector3}    [globalOffset] - Optionally, the globalOffset can be reset in the same step.
   */
  setPlacementTransform(matrix) {

    // Create/Set override placementTransform
    this.#placementTransform = (this.#placementTransform || new LmvMatrix4(true)).copy(matrix);

    // NOTE: The following line is crucial for updating the model matrix on placement transform change.
    //       It is not only required for the DynamicGlobalOffset feature.
    // Update dynamic model matrix based on current placementMatrix and globalOffset
    DynamicGlobalOffset.updateModelMatrix(this, matrix, this.getGlobalOffset());
  }

  /**
   * Change globalOffset that is applied to transform this model from global to viewer coordinates.
   */
  setGlobalOffset(newOffset) {
    this.#globalOffset = this.#globalOffset || new THREE.Vector3();
    this.#globalOffset.copy(newOffset);

    // Update dynamic model matrix based on current placementMatrix and globalOffset
    var pt = this.getPlacementTransform();
    DynamicGlobalOffset.updateModelMatrix(this, pt, newOffset);
  }

  /**
   * Returns the model transform combined with placementWithOffset.
   * It converts the source model coordinate system to viewer coordinates
   * (the coordinates used for rendering, also including subtracted globalOffset)
   *
   * @returns {THREE.Matrix4|null}
   */
  getModelToViewerTransform() {
    if (this.#modelAndPlacementTransform) {// Return cached value if available
      return this.#modelAndPlacementTransform;
    }

    const modelTransform = this.getModelTransform();
    const placementWithOffset = this.getData()?.placementWithOffset;

    if (modelTransform || placementWithOffset) {
      this.#modelAndPlacementTransform = new LmvMatrix4(true);

      if (modelTransform) {
        this.#modelAndPlacementTransform.multiply(modelTransform);
      }
      if (placementWithOffset) {
        this.#modelAndPlacementTransform.multiply(placementWithOffset);
      }
    }

    return this.#modelAndPlacementTransform;
  }

  /**
   * Returns the inverse of the model transform combined with placementWithOffset.
   *
   * @returns {THREE.Matrix4|null}
   */
  getInverseModelToViewerTransform() {
    if (this.#invModelAndPlacementTransform) {// Return cached value if available
      return this.#invModelAndPlacementTransform;
    }

    const tr = this.getModelToViewerTransform();
    if (tr) {
      this.#invModelAndPlacementTransform = tr.clone().invert();
    }

    return this.#invModelAndPlacementTransform;
  }

  /**
   * Returns the inverse of placementWithOffset. Left-multiplying with this transform inverts
   * all transforms that are 'baked' into the mesh transforms by the loader. This excludes the dynamic model transform.
   * May return null if placementWithOffset is null as well.
   *
   * @returns {THREE.Matrix4|null}
   */
  getInversePlacementWithOffset() {
    if (!this.myData.placementWithOffset) {
      return null;
    }

    if (!this.#invPlacementWithOffset) {
      this.#invPlacementWithOffset = new LmvMatrix4(true).copy(this.myData.placementWithOffset).invert();
    }
    return this.#invPlacementWithOffset;
  }

  // Overrides inner state of RenderModel.
  setInnerAttributes(attributes) {
    this.id = attributes._id;
    this.#visibleBounds = attributes._visibleBounds;
    this.#visibleBoundsWithHidden = attributes._visibleBoundsWithHidden;
    this.#tmpBox = attributes._tmpBox;
    this.#numHighlighted = attributes._numHighlighted;
    this.#geoms = attributes._geoms;
    this.#frags = attributes._frags;
    this.#consolidationMap = attributes._consolidationMap;
    this.#renderCounter = attributes._renderCounter;
    this.#frustum = attributes._frustum;
    this.#drawMode = attributes._drawMode;
    this.#idRemap = attributes._idRemap;
    this.#reverseMap = attributes._reverseMap;

    const iterators = {
      _iterator: attributes._iterator,
      _linearIterator: attributes._linearIterator,
      _bvhIterator: attributes._bvhIterator,
      _consolidationIterator: attributes._consolidationIterator
    };

    this.#sceneTraversal.setInnerAttributes(iterators);
  }

  // Get inner state of RenderModel.
  getInnerAttributes() {
    const sceneTraversalAttributes = this.#sceneTraversal.getInnerAttributes();

    return {
      _id: this.id,
      _visibleBounds: this.#visibleBounds,
      _visibleBoundsWithHidden: this.#visibleBoundsWithHidden,
      _tmpBox: this.#tmpBox,
      _numHighlighted: this.#numHighlighted,
      _geoms: this.#geoms,
      _frags: this.#frags,
      _linearIterator: sceneTraversalAttributes.linearIterator,
      _bvhIterator: sceneTraversalAttributes.bvhIterator,
      _iterator: sceneTraversalAttributes.currentIterator,
      _consolidationIterator: sceneTraversalAttributes.consolidationIterator,
      _consolidationMap: this.#consolidationMap,
      _renderCounter: this.#renderCounter,
      _frustum: this.#frustum,
      _drawMode: this.#drawMode,
      _idRemap: this.#idRemap,
      _reverseMap: this.#reverseMap
    };
  }

  /**
   * Changes whether cutplanes should affect the visibility of the model.
   * Works only for 2D models (in OTG the materials are shared).
   * @param {MaterialManager} materialsManager
   * @param {boolean}         doNotCut
   */
  setDoNotCut(materialsManager, doNotCut) {
    if (this.#doNotCut === doNotCut) {
      return;
    }

    this.#doNotCut = doNotCut;
    if (this.#frags) {
      this.#frags.setDoNotCut(doNotCut);
    } else if (this.isLeaflet()) {
      this.#sceneTraversal.setDoNotCutLeaflet(doNotCut);
    }

    const cb = (material) => {
      material.doNotCut = doNotCut;
      const updateNeeded = material.cutplanes?.length > 0 === doNotCut;
      if (updateNeeded) {
        materialsManager._applyCutPlanes(material);
        material.needsUpdate = true;
      }
    };

    materialsManager.forEachInModel(this, false, cb);
  }

  getDoNotCut() {
    return this.#doNotCut;
  }

  /**
   * Sets the viewport bounds for a model, effectively cropping it. Relevant for sheets.
   * @param {MaterialManager} materialsManager
   * @param {THREE.Box3|THREE.Box2|null} bounds - passing null resets the viewport
   */
  setViewportBounds(materialsManager, bounds) {
    if (this.isLeaflet()) {
      this.#sceneTraversal.setViewportBoundsLeaflet(bounds);
    } else if (this.#frags) {
      // For PDFs, there's always a viewport bounds which is the original bounding box (see LmvCanvasContext)
      bounds = bounds || this.isPdf() && this.getModelBounds(true, true);

      // Set bounds in fragment list to update visibility bounds
      this.#frags.setViewBounds(bounds);

      // Set in materials since actual cropping is done in the shader
      materialsManager.setViewportBoundsForModel(this, bounds);
    }

    this.invalidateBBoxes();
  }

  getViewportBounds() {
    return this.isLeaflet() ? this.#sceneTraversal.getViewportBoundsLeaflet() : this.#frags?.getViewBounds();
  }

  /**
   * Dispatches an event to the viewer class.
   *
   * WARNING: this only dispatches events for the main RenderModel instance.
   * LeachViewer instances of the RenderModel do not have a loader and therefore
   * also no viewer reference to dispatch events to.
   *
   * @param {Object} event The event to dispatch
   */
  #dispatchEvent(event) {
    this.#viewer3DImpl?.api.dispatchEvent(event);
  }

  /**
   * Checks if the model is initialized.
   * @returns {boolean} True if the model is initialized, false otherwise.
   *
   * @internal
   */
  isInitialized() {
    return this.#viewer3DImpl !== null;
  }
}